import { type ReactNode, useCallback, useState } from 'react';

import cn from 'classnames';

import { smoothScrollToAnchor } from 'libs/node';

import { Kicker } from 'ui/atoms/Kicker/Kicker';
import { FeatureGrid, type FeatureGridItem, type FeaturesTheme } from 'ui/atoms/FeatureGrid/FeatureGrid';
import { DetailsBlock } from 'ui/molecules/DetailsBlock/DetailsBlock';
import { Button } from 'ui/atoms/Button/Button';

import { ModalFormBook } from 'features/articles/organisms/ModalFormBook';

import styles from './Hero.module.scss';

const baseClass = 'gdb-hero';

type Props = {
  kicker?: ReactNode;
  title: string;
  description?: string;
  features?: FeatureGridItem[];
  featuresTheme?: Exclude<FeaturesTheme, 'transparent'>;
  className?: string;
  footer?: ReactNode;
  rightSide?: ReactNode;
  hasGDBBackground?: boolean;
};

export const Hero = ({
  kicker,
  title,
  description,
  features,
  featuresTheme = 'dark',
  footer,
  className,
  rightSide,
  hasGDBBackground = false,
}: Props) => {
  const [showModal, setShowModal] = useState(false);

  const onSmoothScrollTo = useCallback((ev: React.MouseEvent<HTMLAnchorElement>) => {
    ev.preventDefault();

    const href = ev.currentTarget.href;
    const anchor = href.split('#')[1];
    smoothScrollToAnchor(anchor);
  }, []);

  const onToggleModal = useCallback(() => setShowModal((s) => !s), []);

  return (
    <>
      {showModal && <ModalFormBook title="Book a demo" maxWidth={630} onClose={onToggleModal} />}

      <section
        className={cn(styles[`${baseClass}-wrapper`], className, {
          [styles[`${baseClass}-gdb-background`]]: hasGDBBackground,
        })}
      >
        <div className={styles[`${baseClass}-container`]}>
          <DetailsBlock
            title={title}
            description={description}
            kicker={kicker || <Kicker label="Trusted by 10,000+ companies worldwide" iconType="check-1" />}
            actions={
              <>
                <a href="#pricing" onClick={onSmoothScrollTo}>
                  <Button size="xxl" type="white">
                    Get Started
                  </Button>
                </a>

                <Button size="xxl" type="invert" onClick={onToggleModal}>
                  Book a demo
                </Button>
              </>
            }
            theme={featuresTheme === 'stats-card' ? 'dark' : featuresTheme}
            rightSide={rightSide}
          />

          {features && (
            <FeatureGrid items={features} theme={featuresTheme} className={styles[`${baseClass}-features`]} />
          )}

          {footer}
        </div>
      </section>
    </>
  );
};
